import { useCallback, useEffect, useState } from "react";
import {
  adminService,
  getAllAdmins,
  getOrgById,
} from "../../../../services/api/settings";
import {
  OrgSuperAdmin,
  SuperAdminResponse,
} from "../../../../store/types/settings";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { ErrorWarning } from "../../../../store/types/misc";

// get request new style
interface UseOrgAdminsReturn {
  organizations: OrgSuperAdmin[];
  loading: boolean;
  error: string | null;
  pagination: {
    total: number;
    page: number;
    totalPages: number;
  } | null;
  refetch: (params?: Record<string, any>) => Promise<void>;
}

export const getOrgAdmins = (autoFetch = true): UseOrgAdminsReturn => {
  const [organizations, setOrganizations] = useState<OrgSuperAdmin[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] =
    useState<UseOrgAdminsReturn["pagination"]>(null);

  const fetchOrganizations = useCallback(
    async (params?: Record<string, any>) => {
      setLoading(true);
      setError(null);

      try {
        const response = await getAllAdmins.getAll(params);

        if (response.status === "success") {
          setOrganizations(response?.data?.organizations);
          setPagination({
            total: response.data.total,
            page: response.data.page,
            totalPages: response.data.totalPages,
          });
        } else {
          throw new Error(response.message || "Failed to fetch organizations");
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "An error occurred while fetching organizations";
        setError(errorMessage);
        console.error("Error fetching organizations:", err);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (autoFetch) {
      fetchOrganizations();
    }
  }, [autoFetch, fetchOrganizations]);

  return {
    organizations,
    loading,
    error,
    pagination,
    refetch: fetchOrganizations,
  };
};

export const viewOrg = (id: string) => {
  const [org, setOrgs] = useState<OrgSuperAdmin | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOrg = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getOrgById(id);
      setOrgs(response.data);
    } catch (err) {
      const errorMessage = "Failed to fetch org details";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchOrg();
    }
  }, [id]);

  return { org, loading, error, refetch: fetchOrg };
};

export type Tier = "STARTER" | "GROWTH" | "SCALE" | "ENTERPRISE" | "";
export type VerifyAdminData = {
  tier: Tier;
  organizationId?: string;
};
export type VerifyAdminErrors = {
  tier: string;
};
export const useVerifyAdmin = (initialOrganizationId?: string) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<VerifyAdminData>({
    tier: "",
    organizationId: initialOrganizationId,
  });
  const [errors, setErrors] = useState<VerifyAdminErrors>({
    tier: "",
  });

  const onChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = (): boolean => {
    const newErrors: VerifyAdminErrors = {
      tier: "",
    };

    if (!data.tier) {
      newErrors.tier = "Please select a tier";
    }

    setErrors(newErrors);
    return !newErrors.tier;
  };

  const reset = () => {
    setData({
      tier: "",
      organizationId: initialOrganizationId,
    });
    setErrors({ tier: "" });
  };

  return {
    data,
    loading,
    errors,
    onChangeSelect,
    validate,
    reset,
    setData,
  };
};
